import { MediumIcon } from '@factorialco/deprecated-design-system'
import {
  BillingAddonReferenceFragment,
  CheckSubscriptionItemsInstalledQuery,
  NextInvoiceQuery,
} from 'generated/resources/graphql'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'

import type { F25AddonDTO, PeriodUnit, VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'

import {
  getValidChargeType,
  getValidCurrency,
  getValidPeriodUnit,
  getValidPricingModel,
  isValidIconMediumType,
  isValidTier,
} from 'modules/upsell/lib/typesValidation'

export const DATO_PLAN_ICON_FIELD = 'iconId'
export const DATO_PLAN_ID_FIELD = 'internalTitle'
export const DATO_PLAN_TITLE_FIELD = 'title'
export const DATO_PLAN_DESCRIPTION_FIELD = 'description'
export const DATO_PLAN_SEG_OPTION_FIELD = 'segmentationOption'
export { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/constants'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/constants'
export const STARTER_PLAN_TYPE = 'Starter'
export const PRO_PLAN_TYPE = 'Pro'
export const MONTH_PERIOD = 'month'
export const YEAR_PERIOD = 'year'
export const SIX_MONTH_PERIOD = 'six_months'
export const DEFAULT_CURRENCY = 'EUR'
export const DEFAULT_ICON: MediumIcon = 'logo-avatar'

const ONE_PLAN_IDENTIFIERS = ['one growth', 'one scale', 'one starter'] as const

export const isOnePlan = (internalTitle: string): boolean =>
  ONE_PLAN_IDENTIFIERS.some((id) => internalTitle.toLowerCase().includes(id))

const ISO_COUNTRY_CODES = new Set([
  'us',
  'mx',
  'br',
  'ar',
  'co',
  'es',
  'fr',
  'de',
  'gb',
  'it',
  'pt',
  'nl',
  'se',
  'ch',
  'be',
  'at',
  'cl',
  'bg',
  'hr',
  'cy',
  'cz',
  'ee',
  'gr',
  'hu',
  'lv',
  'lt',
  'mt',
  'pl',
  'ro',
  'sk',
  'si',
  'dk',
  'fi',
  'ie',
  'lu',
  'ea',
  'ic',
  'mo',
  'qa',
  'is',
  'sg',
  'au',
  'hk',
  'sm',
  'ca',
  'il',
  'jp',
  'nz',
  'ae',
  'bs',
  'pr',
  'kr',
  'kw',
])

// https://docs.google.com/spreadsheets/d/1J7mUZbYMJMZqkMhPUHQY5qbeG-VL5lILm_N_nHnWmK8/edit?gid=1079667599#gid=1079667599
const COUNTRY_MAPPING: Record<string, string> = {
  ie: 'de',
  al: 'es',
  bg: 'es',
  hr: 'es',
  cy: 'es',
  cz: 'es',
  ee: 'es',
  gr: 'es',
  hu: 'es',
  lv: 'es',
  lt: 'es',
  mt: 'es',
  pl: 'es',
  ro: 'es',
  sk: 'es',
  si: 'es',
  tr: 'es',
  at: 'de',
  be: 'de',
  dk: 'de',
  fi: 'de',
  lu: 'de',
  nl: 'de',
  se: 'de',
  ch: 'de',
  mc: 'de',
  no: 'de',
}

export function getVariantFromID(id: string): VariantsEnum {
  if (id.includes('Enterprise')) return ENTERPRISE_VARIANT
  return BUSINESS_VARIANT
}

const periodMap: Record<string, PeriodUnit> = {
  month: 'month',
  year: 'year',
  '6_m': 'six_months',
}

export const getCurrentPeriod = (plan: string): PeriodUnit | undefined => {
  const key = Object.keys(periodMap).find((pattern) => plan.includes(pattern))
  return key ? periodMap[key] : undefined
}

export const mapToF25Addon = (addons: readonly BillingAddonReferenceFragment[]): F25AddonDTO[] => {
  return addons.map((addon) => ({
    internalTitle: addon.internalTitle,
    title: addon.title,
    description: addon.description,
    segmentationOption: undefined,
    minimumSeats: undefined,
    iconId: isValidIconMediumType(addon.iconId) ? addon.iconId : DEFAULT_ICON,
    regions: addon.regions.map((region) => ({
      chargebeeAddons:
        region.chargebeeAddons.map((add) => ({
          addonId: add.addonId,
          chargeType: getValidChargeType(add.chargeType),
          currencyCode: getValidCurrency(add.currencyCode),
          period: add.period,
          periodUnit: getValidPeriodUnit(add.periodUnit),
          pricingModel: getValidPricingModel(add.pricingModel),
          price: add.price || null,
          originalPrice: add.originalPrice ?? null,
          selectedCountries: add.selectedCountries || undefined,
          tiers:
            add.tiers
              ?.map((tier) => ({
                price: tier.price,
                startingUnit: tier.startingUnit,
                endingUnit: tier.endingUnit || undefined,
              }))
              .filter(isValidTier) || [],
        })) || [],
      stairStepUsageLimits: [],
      perUnitUsageLimits: [],
      features: region.features.map((feature) => ({
        title: feature.title,
        featureStatus: feature.featureStatus || undefined,
        tooltip: feature.tooltip || undefined,
      })),
    })),
  }))
}

export const formatDate = (date: Date) => {
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const getCountryFromPlan = (plan: string, defaultCountry: string): string => {
  const match = plan.match(/-([a-z]{2})-/i)
  const code = match && match[1] ? match[1].toLowerCase() : defaultCountry.toLowerCase()

  const mappedCode = COUNTRY_MAPPING[code] || code

  return ISO_COUNTRY_CODES.has(mappedCode) ? mappedCode : defaultCountry.toLowerCase()
}

const FREE_PLAN_VALUE = '__free__'

const ONE_TIER_RANK: Record<string, number> = {
  'one starter': 0,
  'one growth': 1,
  'one mid': 2,
  'one scale': 3,
}

export const getDefaultVariant = (plan: Plan): VariantsEnum | undefined => {
  if (plan.variantTypes.includes(ENTERPRISE_VARIANT)) return ENTERPRISE_VARIANT
  if (plan.variantTypes.includes(BUSINESS_VARIANT)) return BUSINESS_VARIANT
  return undefined
}

const getOneTierRank = (internalTitle: string): number => {
  const titleLower = internalTitle.toLowerCase()
  for (const [key, rank] of Object.entries(ONE_TIER_RANK)) {
    if (titleLower.includes(key)) return rank
  }
  return -1
}

const getMaxInstalledOnePlan = (plans: Plan[]): Plan | undefined => {
  if (plans.length === 0) return undefined
  return plans.reduce((max, plan) => {
    return getOneTierRank(plan.internalTitle) > getOneTierRank(max.internalTitle) ? plan : max
  })
}

const getSelectableOnePlans = (onePlans: Plan[], installed: Plan[]): Plan[] => {
  return onePlans.filter(
    (plan) => !installed.some((inst) => inst.internalTitle === plan.internalTitle)
  )
}

const installedOnePlans = (
  subscriptionItemsData: CheckSubscriptionItemsInstalledQuery,
  onePlans: Plan[],
  installedOnePlan: Plan | undefined,
  period: PeriodUnit
) => {
  const items = subscriptionItemsData?.billing?.subscriptionItemsConnection?.nodes || []
  const allItemIds = items.flatMap((item) => item.itemIds || [])
  if (allItemIds.length === 0) return installedOnePlan ? [installedOnePlan] : []

  const matched: Plan[] = []
  for (const plan of onePlans) {
    const planIds = [plan.pricing.getPlanId(period), ...plan.pricing.getAllPlanIds(period)].filter(
      (id): id is string => Boolean(id)
    )

    const isInSubscription = allItemIds.some((subId) => matchesInstalledAddonId(subId, planIds))
    if (isInSubscription) matched.push(plan)
  }

  return matched.length > 0 ? matched : installedOnePlan ? [installedOnePlan] : []
}

const installedOnePlan = (
  onePlans: Plan[],
  installedAddonInternalId: string | null,
  period: PeriodUnit
) => {
  return onePlans.find((plan) => {
    if (!installedAddonInternalId) return false

    const planIds = [plan.pricing.getPlanId(period), ...plan.pricing.getAllPlanIds(period)].filter(
      (id): id is string => Boolean(id)
    )

    return matchesInstalledAddonId(installedAddonInternalId, planIds)
  })
}

const matchesInstalledAddonId = (installedAddonId: string, planIds: string[]): boolean => {
  const normalizedInstalledAddonId = installedAddonId.toLowerCase().trim()

  return planIds.some((planId) => {
    const normalizedPlanId = planId.toLowerCase().trim()
    return (
      normalizedPlanId === normalizedInstalledAddonId ||
      normalizedPlanId.includes(normalizedInstalledAddonId) ||
      normalizedInstalledAddonId.includes(normalizedPlanId)
    )
  })
}

export interface OneInstallationInfo {
  allInstalledPlans: Plan[]
  maxInstalledPlan: Plan | undefined
  selectablePlans: Plan[]
  nextBillingDate: string | null
}

export const getOneInstallationInfo = ({
  onePlans,
  installedAddonInternalId,
  period,
  subscriptionItemsData,
  nextInvoiceData,
}: {
  onePlans: Plan[]
  installedAddonInternalId: string | null
  period: PeriodUnit
  subscriptionItemsData: CheckSubscriptionItemsInstalledQuery | undefined
  nextInvoiceData: NextInvoiceQuery | undefined
}): OneInstallationInfo => {
  const singleInstalledPlan = installedOnePlan(onePlans, installedAddonInternalId, period)

  const allInstalledPlans = subscriptionItemsData
    ? installedOnePlans(subscriptionItemsData, onePlans, singleInstalledPlan, period)
    : singleInstalledPlan
      ? [singleInstalledPlan]
      : []

  const maxInstalledPlan = getMaxInstalledOnePlan(allInstalledPlans)
  const selectablePlans = getSelectableOnePlans(onePlans, allInstalledPlans)

  const nextBillingAt =
    nextInvoiceData?.billing?.cpNextBillingEstimatesConnection?.nodes[0]?.subscriptionEstimate
      ?.nextBillingAt
  const nextBillingDate = nextBillingAt ? formatDate(new Date(nextBillingAt * 1000)) : null

  return {
    allInstalledPlans,
    maxInstalledPlan,
    selectablePlans,
    nextBillingDate,
  }
}

export const getOnePlanInstallationLabel = (
  plan: Plan,
  info: OneInstallationInfo
): {
  isInstalled: boolean
  isMaxInstalled: boolean
  isDowngrade: boolean
  untilDate: string | null
} => {
  const isInstalled = info.allInstalledPlans.some(
    (installed) => installed.internalTitle === plan.internalTitle
  )
  const isMaxInstalled = info.maxInstalledPlan?.internalTitle === plan.internalTitle
  const isDowngrade = info.maxInstalledPlan
    ? getOneTierRank(plan.internalTitle) < getOneTierRank(info.maxInstalledPlan.internalTitle)
    : false
  const untilDate =
    isInstalled && !isMaxInstalled && info.allInstalledPlans.length > 1
      ? info.nextBillingDate
      : null

  return { isInstalled, isMaxInstalled, isDowngrade, untilDate }
}
